import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import classNames from 'classnames';
import {Common, ViewMode} from '@taopix/taopix-core';
import {MoreIcon, PencilIcon, PlusIcon, ThemeName} from '@taopix/taopix-design-system';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import AddPagesButton from './Actions/AddPagesButton';
import DeletePageButton from './Actions/DeletePageButton';
import AutoLayoutButton from './Actions/AutoLayoutButton';
import {LayoutSelector, PagesSelector} from '../../Selectors';
import useViewMode from '../../Hooks/useViewMode';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import DuplicatePageButton from './Actions/DuplicatePageButton';
import {PageActionButton} from '../Context/PageActions/PageActionsContext';
import {useExperience} from '../Experience/ExperienceProvider';
import EditPageButton from './Actions/EditPageButton';
import AddPictureBoxButton from './Actions/AddPictureBoxButton';
import AddTextBoxButton from './Actions/AddTextBoxButton';
import ShowMoreButton from './Actions/ShowMoreButton';

type PageActionButtonsTypes = {
  enableButtons?: boolean;
  pageNumber: number;
  buttonProps: Partial<ButtonProps>;
  buttons: PageActionButton[];
};

const PageActionButtons = ({
  enableButtons = true,
  pageNumber,
  buttonProps,
  buttons,
}: PageActionButtonsTypes) => {
  const ui = useUiState();
  const [viewMode] = useViewMode();
  const {experience} = useExperience();
  const productLayout = useSelector(LayoutSelector);
  const pages = useSelector(PagesSelector);

  // grab the page from the store
  const page = pages.find(p => p.pagenumber === pageNumber);

  const showMoreSettings = useMemo(() => {
    switch (viewMode) {
      case ViewMode.Grid: {
        return {
          icon:
            experience.EDITOR.workArea.gridView.showMoreButtonIcon === 'dots' ? (
              <MoreIcon />
            ) : (
              <PencilIcon />
            ),
          position: 'right',
        };
      }
      case ViewMode.List: {
        return {
          icon:
            experience.EDITOR.workArea.listView.showMoreButtonIcon === 'dots' ? (
              <MoreIcon />
            ) : (
              <PencilIcon />
            ),
          position: experience.EDITOR.workArea.listView.showMoreButtonPosition,
        };
      }
      case ViewMode.Single: {
        return {
          icon:
            experience.EDITOR.workArea.singlePage.showMoreButtonIcon === 'dots' ? (
              <MoreIcon />
            ) : (
              <PencilIcon />
            ),
          position: experience.EDITOR.workArea.singlePage.showMoreButtonPosition,
        };
      }
      default:
        return {
          icon: <MoreIcon />,
          position: 'right',
        };
    }
  }, [viewMode, experience]);

  const smallScreenMenuButtonClasses = classNames(
    'self-start',
    ViewMode.Grid !== viewMode && 'ml-xxs'
  );

  return (
    <>
      {buttons
        .map(button => {
          switch (button.name) {
            case 'editPage':
              return <EditPageButton buttonProps={buttonProps} page={page} key={button.name} />;
            case 'layout':
              if (page?.locked) {
                return <></>;
              }
              return (
                <AutoLayoutButton
                  buttonProps={buttonProps}
                  key={button.name}
                  disabled={!enableButtons}
                />
              );
            case 'addPictureBox':
              return (
                <AddPictureBoxButton
                  enabled={enableButtons}
                  pageNumber={pageNumber}
                  buttonProps={buttonProps}
                  key={button.name}
                />
              );
            case 'addTextBox':
              return (
                <AddTextBoxButton
                  enabled={enableButtons}
                  pageNumber={pageNumber}
                  buttonProps={buttonProps}
                  key={button.name}
                />
              );
            case 'addPage':
              return (
                <AddPagesButton
                  key={button.name}
                  pageNumber={pageNumber}
                  buttonProps={{
                    ...buttonProps,
                    startIcon: <PlusIcon />,
                    buttonStyle: ui.state.isSmallScreen ? buttonProps.buttonStyle : 'tertiary',
                    badgeContent:
                      ui.state.flags.recommendAddingPictures &&
                      (!Common.isPageCover(pageNumber) ||
                        (Common.isPageCover(pageNumber) && 1 !== productLayout.firstpagenumber))
                        ? '1'
                        : undefined,
                    badgePosition:
                      ui.state.flags.recommendAddingPictures &&
                      (!Common.isPageCover(pageNumber) ||
                        (Common.isPageCover(pageNumber) && 1 !== productLayout.firstpagenumber))
                        ? ui.state.isSmallScreen
                          ? 'icon-right'
                          : 'top-right'
                        : undefined,
                    badgeStyle: ThemeName.Primary,
                    hideLabel:
                      ui.state.isSmallScreen &&
                      button.order <= (ViewMode.Grid === viewMode ? 0 : 2),
                  }}
                  disabled={!enableButtons}
                />
              );
            case 'duplicatePage':
              return (
                <DuplicatePageButton
                  pageNumber={pageNumber}
                  buttonProps={buttonProps}
                  disabled={!enableButtons}
                  key={button.name}
                />
              );
            case 'deletePage':
              return (
                <DeletePageButton
                  pageNumber={pageNumber}
                  buttonProps={buttonProps}
                  disabled={!enableButtons}
                  key={button.name}
                />
              );
            case 'showMore':
              return (
                <ShowMoreButton
                  pageNumber={pageNumber}
                  icon={showMoreSettings.icon}
                  classNames={classNames(
                    smallScreenMenuButtonClasses,
                    showMoreSettings.position === 'left' ? 'order-first' : ''
                  )}
                  enabled={enableButtons}
                  key={button.name}
                />
              );
            default:
              return null;
          }
        })
        .filter(c => c)}
    </>
  );
};

export default PageActionButtons;
